import { apiRequest, authToken } from "./client";
import { drainPending } from "../lib/offlineSync";
import { pending } from "../lib/offlineStore";
import { clearAttribution, storedAttribution } from "../lib/attribution";
import { trackSignup } from "../lib/adTags";
import { clearCache } from "../lib/offlineCache";
import { clearActiveExam } from "../lib/activeExam";

export interface OtpRequestResult {
  resendAfterSeconds: number;
  sendsRemaining: number;
}

export interface OtpVerifyResult {
  verified: true;
  registered: boolean;
  email?: string;
  token?: string;
  expiresInSeconds?: number;
  user?: { id: string; firstName: string | null; email: string | null };
  student?: { id: string; onboardingCompleted: boolean } | null;
}

/**
 * Ask for a verification code, sent to the student's email.
 *
 * No channel picker — the code always goes to email.
 */
export function requestOtp(
  email: string,
  options: {
    purpose?: "signup" | "login" | "email_claim" | "password_reset";
  } = {},
): Promise<OtpRequestResult> {
  return apiRequest<OtpRequestResult>("/api/auth/otp/request", {
    method: "POST",
    body: { email, ...options },
  });
}

/** Verify, and keep the token if one comes back. */
export async function verifyOtp(
  email: string,
  code: string,
  purpose: "signup" | "login" | "password_reset" = "signup",
): Promise<OtpVerifyResult> {
  const result = await apiRequest<OtpVerifyResult>("/api/auth/otp/verify", {
    method: "POST",
    body: { email, code, purpose },
  });

  if (result.token) authToken.set(result.token);
  return result;
}

/**
 * Sign out, and leave nothing of this student on the phone.
 *
 * Phones are shared. Their queued offline answers get one last chance to send
 * while the token still works, then the queue is wiped — otherwise the next
 * student to sign in would upload them into their own account.
 */
export async function signOut(): Promise<void> {
  try {
    await drainPending();
  } catch {
    /* Offline: what didn't send is lost with the sign-out, by design. */
  }
  try {
    await pending.clear();
  } catch {
    /* No IndexedDB — nothing was stored. */
  }
  // Saved copies of their profile and readiness go too.
  clearCache();
  clearActiveExam();
  authToken.clear();
}

export interface RegisterInput {
  email: string;
  code: string;
  password: string;
  firstName?: string;
  phone?: string;
  /** Agreed to the Privacy Policy and Terms. The server refuses an account without it. */
  acceptedTerms: boolean;
  /** Opted in to learning-opportunity emails. Off unless ticked. */
  marketingEmailOptIn?: boolean;
}

export interface AuthSession {
  token: string;
  expiresInSeconds: number;
  user: {
    id: string;
    email: string | null;
    phoneNumber?: string | null;
    firstName?: string | null;
  };
  student: { id: string; onboardingCompleted?: boolean } | null;
}

/**
 * Create the account.
 *
 * The code is verified inside this one call rather than by a separate request,
 * so there is no window where a code has been spent but no account exists.
 */
export async function register(input: RegisterInput): Promise<AuthSession> {
  const attribution = storedAttribution();
  const session = await apiRequest<AuthSession>("/api/auth/register", {
    method: "POST",
    body: attribution ? { ...input, attribution } : input,
  });

  authToken.set(session.token);
  // Sent once: the backend keeps the first touch, so this browser is done with it.
  clearAttribution();
  trackSignup();
  return session;
}

/** Email + password, for a returning student. */
export async function login(email: string, password: string): Promise<AuthSession> {
  const session = await apiRequest<AuthSession>("/api/auth/login", {
    method: "POST",
    body: { email, password },
  });

  authToken.set(session.token);
  return session;
}

/** Change password for a logged-in student. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  /*
   * The server signs every device out on a password change and hands this one
   * a fresh token. Store it, or the next request from this phone is signed out
   * too.
   */
  const result = await apiRequest<{ ok: true; token?: string }>("/api/me/password", {
    method: "POST",
    body: { currentPassword, newPassword },
    authenticated: true,
  });
  if (result.token) authToken.set(result.token);
  return { ok: true };
}

/** Reset password using an OTP code (unauthenticated — the OTP is the credential). */
export function resetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("/api/auth/reset-password", {
    method: "POST",
    body: { email, code, newPassword },
  });
}

/**
 * Attach an email to a migrated account that has none, then sign in.
 *
 * Phone + password prove the old account; the code (sent with purpose
 * `email_claim`) proves the new address.
 */
export async function claimEmail(input: {
  phone: string;
  password: string;
  email: string;
  code: string;
}): Promise<AuthSession> {
  const session = await apiRequest<AuthSession>("/api/auth/claim-email", {
    method: "POST",
    body: input,
  });
  authToken.set(session.token);
  return session;
}
